import React from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import DataTable, { SortDirectionEnum, TTableColumn } from '@/components/DataTable';
import { TPaymentListItem } from '../types/ManagePayments';
import { buildRecurringDateText } from '../utils/buildRecurringDateText';
import { getPaymentPresentationStrategy } from '../data/paymentPresentationStrategies';
import PaymentStatusPill from './PaymentStatusPill';
import { batchPaymentsPage } from '@/utils/translations';
import { DATE_FORMAT_WEEKDAY_TIME } from '@/utils/dateFormats';

const CREATED_DATE_COLUMN = 'createdDate';

interface PaymentsTableProps {
  rows: TPaymentListItem[];
  emptyMessage: string;
  isLoading?: boolean;
  onRowClick?: (row: TPaymentListItem) => void;
}

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  rows,
  emptyMessage,
  isLoading,
  onRowClick
}) => {
  const columns: TTableColumn<TPaymentListItem>[] = [
    {
      key: CREATED_DATE_COLUMN,
      header: batchPaymentsPage.columns.createdDate,
      canSort: true,
      // createdDate is null for scheduled_payment rows (not exposed by the backend yet) - those
      // sort last regardless of direction (see DataTable's null handling), rather than falsely
      // claiming to be the oldest or, worse, jumping to the top once sorted descending.
      sortAccessor: (row) => (row.createdDate ? new Date(row.createdDate).getTime() : null),
      cell: (row) =>
        row.createdDate ? moment(row.createdDate).format(DATE_FORMAT_WEEKDAY_TIME) : '-'
    },
    {
      key: 'name',
      header: batchPaymentsPage.columns.to,
      cell: (row) => (
        <div className="flex items-center gap-x-2">
          {getPaymentPresentationStrategy(row.kind).renderAvatar(row)}
          <span>{row.name}</span>
        </div>
      )
    },
    {
      key: 'type',
      header: batchPaymentsPage.columns.type,
      cell: (row) => (
        <div className="flex items-center gap-x-2">
          {getPaymentPresentationStrategy(row.kind).renderTypeLabel(row)}
        </div>
      )
    },
    {
      key: 'scheduledDate',
      header: batchPaymentsPage.columns.scheduledDate,
      // Batch rows never carry an interval (a group has no single interval - each payment inside
      // it does), so they fall through to the plain-date branch, same as a one-off row - see
      // buildRecurringDateText.
      cell: (row) => {
        const { date, recurrence } = buildRecurringDateText(
          row.scheduledDate,
          row.paymentIntervalType,
          row.paymentIntervalValue
        );

        return recurrence ? (
          <span>
            {date} <span className="text-2xs">({recurrence})</span>
          </span>
        ) : (
          date
        );
      }
    },
    {
      key: 'totalAmount',
      header: batchPaymentsPage.columns.amount,
      cell: (row) => (
        <CurrencyFormat
          value={Number(row.totalAmount).toFixed(2)}
          displayType="text"
          thousandSeparator
          prefix="$"
          decimalScale={2}
        />
      )
    },
    {
      key: 'details',
      header: batchPaymentsPage.columns.details,
      cell: (row) => (
        <span>
          {batchPaymentsPage.detailsFromPrefix} {row.payFromAccount?.name ?? '-'}
        </span>
      )
    },
    {
      key: 'state',
      header: batchPaymentsPage.columns.status,
      cell: (row) => <PaymentStatusPill payment={row} />
    }
  ];

  return (
    <DataTable<TPaymentListItem>
      columns={columns}
      data={rows}
      getRowKey={(row) => row.id}
      itemLabel={batchPaymentsPage.title}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      pagination={{ mode: 'none' }}
      sorting={{
        mode: 'client',
        // Latest first by default, across every tab (Scheduled/Action required/Batch history) -
        // not just Action required, since they all share this same table.
        initialSort: { key: CREATED_DATE_COLUMN, direction: SortDirectionEnum.DESC }
      }}
      // Manage Payment-specific per Figma: a compact, left-aligned empty row, not the
      // default centered placeholder in a tall min-height box (that default is unchanged
      // for every other DataTable consumer, e.g. Selected Account).
      emptyMessageAlign="left"
      emptyStateFixedHeight={false}
    />
  );
};

export default PaymentsTable;
